import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { setupTwoFactor, verifyTwoFactor } from '../../services/api';

const C = {
  bg: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceMuted: '#F8FAFC',
  text: '#1E293B',
  textMuted: '#64748B',
  accent: '#0066FF',
  accentDark: '#0052CC',
  accentSoft: '#EFF4FF',
  border: '#E2E8F0',
  red: '#EF4444',
  redSoft: '#FEF2F2',
  redBorder: '#FECACA',
  redText: '#B91C1C',
  green: '#059669',
  greenSoft: '#ECFDF5',
  greenBorder: '#A7F3D0',
};

const HEADING_FONT = "'Syne', sans-serif";
const BODY_FONT    = "'DM Sans', sans-serif";

export default function TwoFactorSetup() {
  const navigate = useNavigate();
  const [qrCode, setQrCode]         = useState(null);
  const [secret, setSecret]         = useState('');
  const [code, setCode]             = useState('');
  const [loading, setLoading]       = useState(true);
  const [verifying, setVerifying]   = useState(false);
  const [error, setError]           = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [done, setDone]             = useState(false);

  // Fetch a fresh QR + secret on mount. Subsequent /setup calls overwrite the
  // pending secret server-side, so reloading the page is the recovery path
  // if the user closes their authenticator without finishing.
  useEffect(() => {
    let cancelled = false;
    setupTwoFactor()
      .then((res) => {
        if (cancelled) return;
        setQrCode(res.data?.qrCode || null);
        setSecret(res.data?.secret || '');
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        setError(status === 401
          ? 'Session expired. Please sign in again.'
          : 'Could not start two-factor setup. Please try again in a moment.');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  async function handleVerify(e) {
    e.preventDefault();
    if (verifying || code.length !== 6) return;
    setVerifying(true);
    setVerifyError('');
    try {
      await verifyTwoFactor(code);
      setDone(true);
      // Brief celebration before going back to the dashboard.
      setTimeout(() => navigate('/parent/dashboard', { replace: true }), 1400);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) setVerifyError('That code didn\'t match. Try the latest 6 digits from your app.');
      else if (status === 400) setVerifyError(err?.response?.data?.error || 'Please enter the 6-digit code from your app.');
      else setVerifyError('Could not verify. Please try again.');
      setVerifying(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: C.bg,
      padding: '48px 16px',
      fontFamily: BODY_FONT,
      color: C.text,
      display: 'flex',
      justifyContent: 'center',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 480,
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        padding: '32px 28px 28px',
        boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
      }}>
        <h1 style={{
          margin: 0,
          fontFamily: HEADING_FONT,
          fontSize: 24,
          fontWeight: 700,
          letterSpacing: '-0.01em',
        }}>Set up two-factor authentication</h1>
        <p style={{ margin: '8px 0 22px', color: C.textMuted, fontSize: 15, lineHeight: 1.55 }}>
          Add a second layer of protection to your Lantern account. You'll need an authenticator app like Google Authenticator, Authy, or 1Password.
        </p>

        {loading && (
          <div style={{ padding: '24px 0', textAlign: 'center', color: C.textMuted }}>
            Preparing your QR code…
          </div>
        )}

        {error && !loading && (
          <div role="alert" style={{
            padding: '12px 16px',
            background: C.redSoft,
            border: `1px solid ${C.redBorder}`,
            borderRadius: 10,
            color: C.redText,
            fontSize: 14,
          }}>{error}</div>
        )}

        {!loading && !error && !done && (
          <>
            <ol style={{
              margin: 0,
              padding: '0 0 0 18px',
              color: C.textMuted,
              fontSize: 14,
              lineHeight: 1.6,
            }}>
              <li>Open your authenticator app.</li>
              <li>Scan the QR code below — or enter the manual code if scanning isn't available.</li>
              <li>Enter the 6-digit code your app shows to confirm.</li>
            </ol>

            {qrCode && (
              <div style={{
                margin: '20px 0 12px',
                padding: 16,
                background: C.surfaceMuted,
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                display: 'flex',
                justifyContent: 'center',
              }}>
                <img
                  src={qrCode}
                  alt="Lantern two-factor QR code"
                  style={{ width: 200, height: 200, imageRendering: 'pixelated' }}
                />
              </div>
            )}

            {secret && (
              <div style={{
                fontSize: 13,
                color: C.textMuted,
                marginBottom: 20,
                textAlign: 'center',
              }}>
                <div style={{ marginBottom: 4 }}>Manual entry code</div>
                <div style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 14,
                  color: C.text,
                  letterSpacing: '0.06em',
                  wordBreak: 'break-all',
                  background: C.accentSoft,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  padding: '8px 12px',
                  display: 'inline-block',
                }}>{secret}</div>
              </div>
            )}

            <form onSubmit={handleVerify} noValidate>
              <label htmlFor="totp-code" style={{
                display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6,
              }}>6-digit code from your app</label>
              <input
                id="totp-code"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => {
                  const next = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(next);
                  setVerifyError('');
                }}
                disabled={verifying}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 22,
                  letterSpacing: '0.4em',
                  textAlign: 'center',
                  color: C.text,
                  background: C.surface,
                  border: `1.5px solid ${C.border}`,
                  borderRadius: 10,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />

              {verifyError && (
                <p role="alert" style={{ margin: '8px 2px 0', fontSize: 13, color: C.redText }}>
                  {verifyError}
                </p>
              )}

              <button
                type="submit"
                disabled={verifying || code.length !== 6}
                style={{
                  marginTop: 16,
                  width: '100%',
                  padding: '12px 18px',
                  background: code.length !== 6 ? '#94A3B8' : C.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  fontFamily: HEADING_FONT,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: verifying || code.length !== 6 ? 'not-allowed' : 'pointer',
                }}
              >
                {verifying ? 'Verifying…' : 'Confirm and enable'}
              </button>
            </form>
          </>
        )}

        {done && (
          <div role="status" style={{
            padding: '16px 18px',
            background: C.greenSoft,
            border: `1px solid ${C.greenBorder}`,
            borderRadius: 10,
            color: '#065F46',
            fontSize: 14,
            lineHeight: 1.55,
          }}>
            Two-factor authentication is now enabled. Redirecting you back to the dashboard…
          </div>
        )}

        <div style={{ marginTop: 22, fontSize: 14 }}>
          <Link to="/parent/dashboard" style={{ color: C.accent, textDecoration: 'none', fontWeight: 600 }}>
            ← Back to dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
